import { create } from 'zustand';
import { createFlow, onSessionChange } from './session-read';
import type {
  CelebrationType,
  DomainCelebrationData,
  CourseCelebrationData,
  EmpathyUnlockCelebrationData,
  QueuedCelebration,
} from '../types';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storage';
import { maybeRequestReview } from '../utils/review-prompt';

// ============================================
// Helper Functions
// ============================================

/**
 * Load shown celebrations from localStorage
 */
function loadShownCelebrations(): Set<string> {
  try {
    const stored = safeGetItem('shownCelebrations');
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (error) {
    console.error('[CelebrationStore] Failed to load from localStorage:', error);
  }
  return new Set();
}

/**
 * Save shown celebrations to localStorage
 */
function saveShownCelebrations(celebrations: Set<string>): void {
  try {
    safeSetItem('shownCelebrations', JSON.stringify(Array.from(celebrations)));
  } catch (error) {
    console.error('[CelebrationStore] Failed to save to localStorage:', error);
  }
}

// ============================================
// Type Definitions
// ============================================

interface CelebrationState {
  isOpen: boolean;
  type: CelebrationType | null;
  data: DomainCelebrationData | CourseCelebrationData | EmpathyUnlockCelebrationData | null;
  celebrationQueue: QueuedCelebration[];
  shownCelebrations: Set<string>;
}

interface CelebrationActions {
  showDomainCelebration: (domainData: DomainCelebrationData) => boolean;
  showCourseCelebration: (courseData: CourseCelebrationData) => boolean;
  showEmpathyUnlockCelebration: () => boolean;
  closeCelebration: () => void;
  /**
   * Keep the modal shut for `ms` (walkthrough audit B7): shows in the
   * meantime are queued in call order (deduped as ever) and the first opens
   * when the hold ends. dismissAll cancels it. A hold while one is running
   * is a no-op: it does not restart the wait.
   */
  holdCelebrations: (ms: number) => void;
  /** Drop the open celebration, the queue, and any queued one about to show (#940). */
  dismissAll: () => void;
  resetShownCelebrations: () => void;
  hasCelebrated: (id: string) => boolean;
}

type CelebrationStore = CelebrationState & CelebrationActions;

// ============================================
// Store Implementation
// ============================================

/**
 * The queued celebration scheduled to show after a close is a flow
 * (session-read.ts createFlow): dismissAll() closes it, and a scheduled show
 * that no longer owns it (dismissed, re-scheduled, or another session) does
 * nothing (#940).
 */
const queuedShow = createFlow();

/**
 * How long the quiz result stays on screen alone before the module card
 * opens over it, so "Quiz Complete 100% · 45/45" can be read (B7).
 */
export const CELEBRATION_OPEN_DELAY_MS = 1500;

/**
 * The hold (holdCelebrations): a flow like queuedShow, so dismissAll and a
 * session change end it. While it is open a show queues instead of opening.
 */
const heldOpen = createFlow();
const isHeld = (): boolean => heldOpen.current() !== null;

export const useCelebrationStore = create<CelebrationStore>((set, get) => ({
  // Modal state
  isOpen: false,
  type: null,
  data: null,

  // Queue for celebrations (show one after another)
  celebrationQueue: [],

  // Track completed celebrations (prevent showing twice)
  shownCelebrations: loadShownCelebrations(),

  /**
   * Show domain completion celebration
   */
  showDomainCelebration: (domainData: DomainCelebrationData): boolean => {
    const celebrationId = `domain-${domainData.domainId}`;
    const { shownCelebrations, isOpen } = get();

    console.log('[CelebrationStore] showDomainCelebration called');
    console.log('[CelebrationStore] Domain data:', domainData);
    console.log('[CelebrationStore] Celebration ID:', celebrationId);
    console.log('[CelebrationStore] Already shown:', shownCelebrations.has(celebrationId));
    console.log('[CelebrationStore] All shown celebrations:', Array.from(shownCelebrations));

    // Don't show if already celebrated
    if (shownCelebrations.has(celebrationId)) {
      console.log('[CelebrationStore] Celebration already shown, skipping');
      return false;
    }

    // Delight moment: a newly-completed unit/module. Gated + capped in the util.
    void maybeRequestReview('first_unit');

    // If a celebration is already open (or held shut), queue this one
    if (isOpen || isHeld()) {
      console.log('[CelebrationStore] Celebration is open, queuing domain celebration');
      const queue = get().celebrationQueue;
      const updatedCelebrations = new Set(shownCelebrations);
      updatedCelebrations.add(celebrationId);
      set({
        celebrationQueue: [
          ...queue,
          {
            type: 'domain',
            data: { ...domainData, quoteIndex: Math.floor(Math.random() * 8) },
            id: celebrationId,
          },
        ],
        shownCelebrations: updatedCelebrations,
      });
      saveShownCelebrations(updatedCelebrations);
      return true;
    }

    console.log('[CelebrationStore] Opening celebration modal');

    // Pick a random quote index once (0-7, there are 8 quotes)
    const quoteIndex = Math.floor(Math.random() * 8);

    const updatedCelebrations = new Set(shownCelebrations);
    updatedCelebrations.add(celebrationId);
    set({
      isOpen: true,
      type: 'domain',
      data: { ...domainData, quoteIndex },
      shownCelebrations: updatedCelebrations,
    });
    saveShownCelebrations(updatedCelebrations);

    return true;
  },

  /**
   * Show course completion celebration
   */
  showCourseCelebration: (courseData: CourseCelebrationData): boolean => {
    const celebrationId = 'course';
    const { shownCelebrations, isOpen } = get();

    // Don't show if already celebrated
    if (shownCelebrations.has(celebrationId)) {
      return false;
    }

    // Delight moment: the whole course is done. Gated + capped in the util.
    void maybeRequestReview('course_complete');

    // If a celebration is already open (or held shut), queue this one
    if (isOpen || isHeld()) {
      console.log('[CelebrationStore] Domain celebration is open, queuing course celebration');
      const queue = get().celebrationQueue;
      const updatedCelebrations = new Set(shownCelebrations);
      updatedCelebrations.add(celebrationId);
      set({
        celebrationQueue: [...queue, { type: 'course', data: courseData, id: celebrationId }],
        shownCelebrations: updatedCelebrations,
      });
      saveShownCelebrations(updatedCelebrations);
      return true;
    }

    const updatedCelebrations = new Set(shownCelebrations);
    updatedCelebrations.add(celebrationId);
    set({
      isOpen: true,
      type: 'course',
      data: courseData,
      shownCelebrations: updatedCelebrations,
    });
    saveShownCelebrations(updatedCelebrations);

    return true;
  },

  /**
   * Show empathy unlock celebration
   */
  showEmpathyUnlockCelebration: (): boolean => {
    const celebrationId = 'empathy-unlock';
    const { shownCelebrations, isOpen } = get();

    if (shownCelebrations.has(celebrationId)) return false;

    if (isOpen || isHeld()) {
      const queue = get().celebrationQueue;
      const updatedCelebrations = new Set(shownCelebrations);
      updatedCelebrations.add(celebrationId);
      set({
        celebrationQueue: [...queue, { type: 'empathy-unlock', data: {}, id: celebrationId }],
        shownCelebrations: updatedCelebrations,
      });
      saveShownCelebrations(updatedCelebrations);
      return true;
    }

    const updatedCelebrations = new Set(shownCelebrations);
    updatedCelebrations.add(celebrationId);
    set({
      isOpen: true,
      type: 'empathy-unlock',
      data: {},
      shownCelebrations: updatedCelebrations,
    });
    saveShownCelebrations(updatedCelebrations);

    return true;
  },

  /**
   * Close celebration modal and show next queued celebration if any
   */
  closeCelebration: (): void => {
    const queue = get().celebrationQueue;

    // Always close immediately to avoid stale state window
    set({
      isOpen: false,
      type: null,
      data: null,
    });

    // Check if there's a queued celebration
    if (queue.length > 0) {
      console.log('[CelebrationStore] Showing next queued celebration');
      const [nextCelebration, ...remainingQueue] = queue;
      set({ celebrationQueue: remainingQueue });

      // Show the queued celebration after a short delay for smooth transition
      const owner = queuedShow.open();
      setTimeout(() => {
        if (!queuedShow.owns(owner)) return; // dismissed meanwhile (session change)
        queuedShow.close();
        const { shownCelebrations } = get();
        const updatedCelebrations = new Set(shownCelebrations);
        updatedCelebrations.add(nextCelebration.id);

        set({
          isOpen: true,
          type: nextCelebration.type,
          data: nextCelebration.data,
          shownCelebrations: updatedCelebrations,
        });
        saveShownCelebrations(updatedCelebrations);
      }, 300);
    }
  },

  holdCelebrations: (ms: number): void => {
    // Idempotent while held (review r1 on #1079): a caller whose effect
    // re-runs must not push the opening back.
    if (isHeld()) return;
    const owner = heldOpen.open();
    setTimeout(() => {
      if (!heldOpen.owns(owner)) return; // dismissed (and maybe re-held), or another session
      heldOpen.close();
      const { isOpen, celebrationQueue } = get();
      if (isOpen || celebrationQueue.length === 0) return;
      const [next, ...rest] = celebrationQueue;
      set({ isOpen: true, type: next.type, data: next.data, celebrationQueue: rest });
    }, ms);
  },

  dismissAll: (): void => {
    heldOpen.close();
    queuedShow.close();
    set({ isOpen: false, type: null, data: null, celebrationQueue: [] });
  },

  /**
   * Reset shown celebrations (for testing/debugging)
   */
  resetShownCelebrations: (): void => {
    set({ shownCelebrations: new Set(), celebrationQueue: [] });
    safeRemoveItem('shownCelebrations');
    console.log('[CelebrationStore] Celebration history cleared');
  },

  /**
   * Check if a celebration has been shown
   */
  hasCelebrated: (id: string): boolean => {
    return get().shownCelebrations.has(id);
  },
}));

// A celebration earned by one account never shows for the next (#940).
onSessionChange(() => useCelebrationStore.getState().dismissAll());
